import type { SuCategory, SuOwnerDef, SuStatusDef, SuOwnerKey, SuStatusKey } from './types';

export const SU_STATUS: Record<SuStatusKey, SuStatusDef> = {
  done: { label: 'Done', tone: 'success', icon: 'check', priority: 5 },
  in_progress: { label: 'ELVA setting up', tone: 'info', icon: 'spin', priority: 3 },
  waiting_on_you: { label: 'Your action needed', tone: 'gold', icon: 'dot', priority: 1 },
  waiting_on_webmaster: { label: 'Waiting on webmaster', tone: 'lav', icon: 'dot', priority: 2 },
  not_started: { label: 'Not started', tone: 'neutral', icon: 'dot', priority: 4 },
  broken: { label: 'Needs attention', tone: 'terra', icon: 'alert', priority: 0 },
};

export const SU_OWNER: Record<SuOwnerKey, SuOwnerDef> = {
  dentist: { label: 'You', tone: 'gold', icon: 'team' },
  webmaster: { label: 'Webmaster', tone: 'lav', icon: 'bolt' },
  elva: { label: 'ELVA', tone: 'ai', icon: 'sparkle' },
};

export const SU_CATEGORIES: SuCategory[] = [
  {
    id: 'google_ads',
    title: 'Google Ads',
    description: 'Connection, billing, and conversion tracking',
    icon: 'target',
    tasks: [
      {
        id: 1,
        taskKey: 'google_ads_linked',
        title: 'Google Ads account linked',
        status: 'done',
        owner: 'elva',
        detail: 'ELVA has API access to your Google Ads account (ID: 234-567-8901)',
      },
      {
        id: 2,
        taskKey: 'billing_attached',
        title: 'Billing account attached',
        status: 'done',
        owner: 'dentist',
        detail: "You're billed directly by Google. ELVA does not handle ad spend.",
      },
      {
        id: 3,
        taskKey: 'conversion_tracking',
        title: 'Conversion tracking configured',
        status: 'done',
        owner: 'elva',
        detail: 'Offline conversion upload active via GCLID matching with PMS data',
      },
      {
        id: 4,
        taskKey: 'initial_campaigns',
        title: 'Initial campaign structure built',
        status: 'in_progress',
        owner: 'elva',
        detail: 'ELVA is setting up your first 6 campaigns based on local market analysis',
      },
    ],
  },
  {
    id: 'landing_pages',
    title: 'Landing Pages',
    description: 'Custom subdomain and hosting',
    icon: 'upload',
    tasks: [
      {
        id: 5,
        taskKey: 'cname_dns',
        title: 'CNAME DNS record added',
        status: 'waiting_on_webmaster',
        owner: 'webmaster',
        detail: 'Add CNAME: go.smilecentraldental.com → lp.elva.ai',
      },
      {
        id: 6,
        taskKey: 'ssl_provisioned',
        title: 'SSL certificate provisioned',
        status: 'not_started',
        owner: 'elva',
        detail: 'ELVA auto-provisions once CNAME resolves',
      },
      {
        id: 7,
        taskKey: 'first_landing_page',
        title: 'First landing page generated',
        status: 'done',
        owner: 'elva',
        detail: 'Dental Implants landing page ready for approval',
      },
    ],
  },
  {
    id: 'seo',
    title: 'SEO & Website Integration',
    description: 'Access and integration with your main website',
    icon: 'search',
    tasks: [
      {
        id: 8,
        taskKey: 'website_crawl',
        title: 'Website crawl access verified',
        status: 'done',
        owner: 'elva',
        detail: 'ELVA crawls smilecentraldental.com every 6 hours · 14 pages detected',
      },
      {
        id: 9,
        taskKey: 'gsc_connected',
        title: 'Google Search Console connected',
        status: 'done',
        owner: 'dentist',
        detail: 'Read-only access granted March 3',
      },
      {
        id: 10,
        taskKey: 'ga4_linked',
        title: 'Google Analytics linked',
        status: 'done',
        owner: 'dentist',
        detail: 'Analytics 4 property connected · 90 days of data imported',
      },
      {
        id: 11,
        taskKey: 'schema_injector',
        title: 'Schema Injector script installed',
        status: 'waiting_on_webmaster',
        owner: 'webmaster',
        detail: 'Single <script> tag to paste before </head> · Enables automatic schema deployment',
      },
    ],
  },
  {
    id: 'gbp',
    title: 'Google Business Profile',
    description: 'Management access for your GBP listing',
    icon: 'home',
    tasks: [
      {
        id: 12,
        taskKey: 'gbp_connected',
        title: 'GBP management access granted',
        status: 'done',
        owner: 'dentist',
        detail: 'ELVA has Manager role on your GBP listing',
      },
      {
        id: 13,
        taskKey: 'gbp_business_info',
        title: 'Business info verified',
        status: 'waiting_on_you',
        owner: 'dentist',
        detail: 'Review current NAP and service categories — some fields may be outdated',
      },
    ],
  },
  {
    id: 'calls',
    title: 'Call Tracking',
    description: 'Phone number pool and routing',
    icon: 'phone',
    tasks: [
      {
        id: 14,
        taskKey: 'call_tracking_pool',
        title: 'Phone number pool provisioned',
        status: 'done',
        owner: 'elva',
        detail: '10 tracking numbers reserved · (212) 555-0141 through 0150',
      },
      {
        id: 15,
        taskKey: 'ai_receptionist_routing',
        title: 'AI Receptionist routing configured',
        status: 'done',
        owner: 'elva',
        detail: 'All tracking numbers route to your AI Receptionist with ad attribution',
      },
    ],
  },
  {
    id: 'market_intel',
    title: 'Market Intelligence',
    description: 'Competitor and local market context',
    icon: 'globe',
    tasks: [
      {
        id: 16,
        taskKey: 'competitor_list',
        title: 'Competitor list identified',
        status: 'done',
        owner: 'elva',
        detail: '8 competitors within 5-mile radius · Dossiers being built',
      },
      {
        id: 17,
        taskKey: 'market_research',
        title: 'Market research report generated',
        status: 'in_progress',
        owner: 'elva',
        detail: 'ELVA is analyzing your local dental market · Estimated 2-3 days',
      },
    ],
  },
];
